import React, { useRef, useEffect, useState } from "react";
import Player from "@vimeo/player";
import styles from "./FrameWrapperVideo.module.scss";
import IconClose from "../../common/icons/IconClose";
import { OFFCOURSE_API_BASE_URL } from "../../config";
import CryptoJS from "crypto-js";

const FrameWrapperVideo = ({
  url,
  startTime,
  endTime,
  closePopup,
  user,
  token,
  event,
  userEventsAccess,
}) => {
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const ctaTimeoutRef = useRef(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showCTA, setShowCTA] = useState(false);

  // Convert time string (HH:MM:SS) to seconds
  const convertTimeToSeconds = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;

    // Supprime les millisecondes s'il y en a
    const cleanTime = timeStr.split(".")[0];

    const parts = cleanTime.split(":").map(Number);

    let seconds = 0;

    if (parts.length === 3) {
      // hh:mm:ss
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // mm:ss
      seconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      // ss
      seconds = parts[0];
    }

    return Number.isFinite(seconds) ? seconds : 0;
  };

  const handleCTAClose = () => {
    setShowCTA(false);
  };

  // Extract Vimeo ID from URL
  const getVimeoId = () => {
    if (!url) return null;
    return url.split("/").pop().split("?")[0];
  };

  const showCallToAction = () => {
    if (ctaTimeoutRef.current) {
      clearTimeout(ctaTimeoutRef.current);
    }

    setShowCTA(true);
  };

  const hideCallToAction = () => {
    setShowCTA(false);
    if (ctaTimeoutRef.current) {
      clearTimeout(ctaTimeoutRef.current);
    }
  };

  const handleCTAClick = () => {
    const hashInput = `${user?.id}FFFCONTROL`;
    const md5Hash = CryptoJS.MD5(hashInput).toString();
    const redirectUrl = `${OFFCOURSE_API_BASE_URL}/autolog?idApi2=${user?.id}&check=${md5Hash}&eventId=${event}`;

    window.open(redirectUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {}, []);

  // Initialize Vimeo Player
  useEffect(() => {
    const vimeoId = getVimeoId();
    if (!vimeoId || !videoContainerRef.current) return;

    // Convert times to seconds
    const START = convertTimeToSeconds(startTime);
    const END = convertTimeToSeconds(endTime);

    // Create player instance
    playerRef.current = new Player(videoContainerRef.current, {
      id: vimeoId,
      autoplay: false, // We'll handle autoplay after setting time
      byline: false,
      title: false,
      portrait: false,
      dnt: true,
      width: 1280,
      height: 720,
    });

    // Set up event listeners
    playerRef.current.on("loaded", () => {
      setIsPlayerReady(true);

      // Set start time and play
      playerRef.current.setCurrentTime(START).then(() => {
        playerRef.current.play();
      });
    });

    if (!userEventsAccess?.[event] && !user?.type?.includes("ADMIN")) {
      playerRef.current.on("ended", () => {
        showCallToAction(); // Show CTA when video ends
      });

      playerRef.current.on("timeupdate", (data) => {
        const currentTime = data.seconds;

        // If video goes before start time, jump to start and show CTA
        if (currentTime < START) {
          playerRef.current.setCurrentTime(START);
          showCallToAction();
        }

        // If video reaches or goes beyond end time, pause and jump back to start and show CTA
        if (END > 0 && currentTime >= END) {
          playerRef.current.pause();
          playerRef.current.setCurrentTime(START);
          showCallToAction();
        }
      });
    }

    playerRef.current.on("error", (error) => {
      console.error("Vimeo Player error:", error);
    });

    // Cleanup function
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy().catch(console.error);
        playerRef.current = null;
      }
      // Clear timeout on unmount
      if (ctaTimeoutRef.current) {
        clearTimeout(ctaTimeoutRef.current);
      }
    };
  }, [url, startTime, endTime]);

  // Prevent scroll when modal is open
  useEffect(() => {
    const handleScroll = (e) => {
      if (videoContainerRef.current?.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("wheel", handleScroll, { passive: false });
    return () => document.removeEventListener("wheel", handleScroll);
  }, []);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => e.key === "Escape" && closePopup();
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closePopup]);

  // Handle close - pause video before closing
  const handleClose = () => {
    if (playerRef.current) {
      playerRef.current.pause().catch(console.error);
    }
    closePopup();
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div
        className={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.videoWrapper}>
          <div
            ref={videoContainerRef}
            id="vimeo-excerpt"
            className={styles.vimeoExcerpt}
          />
          {!isPlayerReady && (
            <div className={styles.loadingState}>Loading video...</div>
          )}

          {/* Call to Action Overlay */}
          {showCTA && (
            <div className={styles.ctaOverlay}>
              <button
                className={styles.ctaCloseButton}
                onClick={handleCTAClose}
                aria-label="Fermer"
              />
              <div className={styles.ctaContent}>
                <h3>Webinaire Complet Disponible</h3>
                <p>Regardez l'intégralité du webinaire sur notre plateforme</p>
                <button className={styles.ctaButton} onClick={handleCTAClick}>
                  Accéder au webinaire complet
                </button>
              </div>
            </div>
          )}
        </div>
        <button className={styles.closeButton} onClick={handleClose}>
          <IconClose />
        </button>
      </div>
    </div>
  );
};

export default FrameWrapperVideo;
